/**
 * AI Arena Service
 * Orchestrates round-robin AI vs AI tournaments with ELO tracking and blunder analysis.
 * Uses its own ZodiacEngine instances (not the singleton) to avoid interfering with other pages.
 */

import { ZodiacEngine } from '../zodiacEngine/zodiacEngine';
import { State, PieceType } from '../zodiacEngine/types';
import { AIOrchestrator } from '../zodiacEngine/AI/Core/AIOrchestrator';
import { AIPlayerType } from '../zodiacEngine/AI/Core/IAIPlayer';
import {
  AIModel,
  AI_MODEL_CONFIG,
  getBaseAITypeFromModel,
} from '../zodiacEngine/AI/Core/AIVariants';
import { ZodiacFEN } from '../zodiacEngine/Fen/ZodiacFEN';
import { SentinelEvaluator } from '../zodiacEngine/AI/MinimaxAI/SentinelEvaluator';
import { AIArenaFirebaseService } from './AIArenaFirebaseService';
import { AIGameDoc, AITournamentDoc, KeyPosition, NotableMove, WinCondition } from './AIAnalyticsTypes';
import { PieceStatsCollector } from '../bots/pieceStats/PieceStatsCollector';
import { ExperienceWeightedSelector } from '../bots/pieceStats/ExperienceWeightedSelector';
import { PieceStatsCache } from '../bots/pieceStats/PieceStatsCache';
import { selectPieceStrategic, selectKingRandom } from '../zodiacEngine/AI/MinimaxAI/PieceSelector';
import { hasAllyAdjacencyPrefs } from '../bots/pieceStats/LaningHeuristics';
import { KING_RACE_SCORES } from '../zodiacEngine/AI/WispAI/index';

// --- Types ---

export type ArenaStatus = 'idle' | 'running' | 'paused' | 'finished' | 'continuous';

export interface ArenaVariant {
  model: AIModel;
  label: string; // e.g. "Sentinel" or "Sentinel (2s)"
  timeLimit?: number; // Per-variant time limit in ms (defaults to orchestrator cap)
  timeFrame?: string; // e.g. "fast", "standard", "deep" — used for Firebase variant ID
}

export interface VariantRating {
  variant: ArenaVariant;
  rating: number;
  games: number;
  wins: number;
  losses: number;
  draws: number;
  blunders: number;
}

export interface BlunderRecord {
  variantLabel: string;
  gameIndex: number;
  moveNumber: number;
  confidence: number;
  reasoning: string;
  opponent: string;
}

export interface MatchResult {
  lightVariant: string;
  darkVariant: string;
  winner: 'light' | 'dark' | 'draw';
  winCondition: WinCondition;
  moves: number;
  lightBlunders: number;
  darkBlunders: number;
  lightBrilliants: number;
  darkBrilliants: number;
  keyPositions?: KeyPosition[];
  notableMoves?: NotableMove[];
  fenHistory?: string[];
  evalHistory?: number[];  // Per-move board eval from Light's perspective
  lightKing?: string;
  darkKing?: string;
}

export interface ArenaState {
  status: ArenaStatus;
  ratings: VariantRating[];
  blunders: BlunderRecord[];
  matchHistory: MatchResult[];
  currentMatchup: string;
  currentMoveCount: number;
  totalMatchups: number;
  completedMatchups: number;
  currentGameInMatchup: number;
  gamesPerMatchup: number;
}

type ArenaListener = (state: ArenaState) => void;

// --- ELO helpers (simplified, no Firebase) ---

const START_RATING = 1300;
const MAX_EXCHANGE = 30;
const FALLOFF_THRESHOLD = 300;

function calculateWinProbability(ratingA: number, ratingB: number): number {
  return 1 / (1 + Math.pow(10, (ratingB - ratingA) / 400));
}

function calculateRatingExchange(
  ratingA: number,
  ratingB: number,
  result: 'a' | 'b' | 'draw'
): { changeA: number; changeB: number } {
  const diff = Math.abs(ratingA - ratingB);
  if (diff > FALLOFF_THRESHOLD) return { changeA: 0, changeB: 0 };

  const expectedA = calculateWinProbability(ratingA, ratingB);
  const actualA = result === 'a' ? 1 : result === 'draw' ? 0.5 : 0;
  const change = Math.round(MAX_EXCHANGE * (actualA - expectedA));
  return { changeA: change, changeB: -change };
}

// --- Service ---

export class ArenaService {
  private state: ArenaState;
  private listeners: Set<ArenaListener> = new Set();
  private abortController: AbortController | null = null;
  private safetyMoveLimit = 200; // prevents infinite loops if AI gets stuck
  private tournamentId: string = '';
  private firebaseEnabled = true;
  private gameTimeoutMs = 90_000; // 90s per game — kills stuck AI
  private lastGameCompletedAt = 0;
  private watchdogTimer: ReturnType<typeof setInterval> | null = null;
  private continuousVariants: ArenaVariant[] = [];
  private continuousConcurrency = 1;

  constructor() {
    this.state = this.createEmptyState(5);
  }

  // --- Public API ---

  getState(): ArenaState {
    return this.state;
  }

  subscribe(listener: ArenaListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  async startTournament(
    variants: ArenaVariant[],
    gamesPerMatchup: number
  ): Promise<void> {
    if (this.state.status === 'running') return;

    this.tournamentId = `arena-${Date.now()}`;

    // Build matchup list (round robin, each pair plays both sides)
    const matchups: { light: ArenaVariant; dark: ArenaVariant }[] = [];
    for (let i = 0; i < variants.length; i++) {
      for (let j = 0; j < variants.length; j++) {
        if (i === j) continue;
        matchups.push({ light: variants[i], dark: variants[j] });
      }
    }

    // Init ratings, seeded from Firebase if available
    const ratings: VariantRating[] = variants.map(v => ({
      variant: v,
      rating: START_RATING,
      games: 0,
      wins: 0,
      losses: 0,
      draws: 0,
      blunders: 0,
    }));

    // Seed from Firebase so ELO continues from where it left off
    try {
      const fbRatings = await AIArenaFirebaseService.getModelRatings();
      for (const fb of fbRatings) {
        const match = ratings.find(r =>
          this.makeVariantId(r.variant) === fb._docId
        );
        if (match) {
          match.rating = fb.rating;
          match.games = fb.games;
          match.wins = fb.wins;
          match.losses = fb.losses;
          match.draws = fb.draws;
          match.blunders = Math.round(fb.avgBlundersPerGame * fb.games);
        }
      }
    } catch { /* start fresh if Firebase unavailable */ }

    this.state = {
      status: 'running',
      ratings,
      blunders: [],
      matchHistory: [],
      currentMatchup: '',
      currentMoveCount: 0,
      totalMatchups: matchups.length * gamesPerMatchup,
      completedMatchups: 0,
      currentGameInMatchup: 0,
      gamesPerMatchup,
    };
    this.notify();

    this.abortController = new AbortController();
    const signal = this.abortController.signal;

    try {
      for (const matchup of matchups) {
        for (let g = 0; g < gamesPerMatchup; g++) {
          if (signal.aborted) return;
          while (this.state.status === 'paused') {
            await this.sleep(200);
            if (signal.aborted) return;
          }

          this.state.currentMatchup = `${matchup.light.label} vs ${matchup.dark.label}`;
          this.state.currentGameInMatchup = g + 1;
          this.state.currentMoveCount = 0;
          this.notify();

          const result = await this.playGame(matchup.light, matchup.dark, signal);
          if (!result || signal.aborted) return;

          // Record result
          this.state.matchHistory.push(result);

          // Update ELO
          const lightRating = ratings.find(r => r.variant.label === matchup.light.label)!;
          const darkRating = ratings.find(r => r.variant.label === matchup.dark.label)!;

          const eloResult = result.winner === 'light' ? 'a' : result.winner === 'dark' ? 'b' : 'draw';
          const exchange = calculateRatingExchange(lightRating.rating, darkRating.rating, eloResult);
          lightRating.rating += exchange.changeA;
          darkRating.rating += exchange.changeB;

          lightRating.games++;
          darkRating.games++;
          lightRating.blunders += result.lightBlunders;
          darkRating.blunders += result.darkBlunders;

          if (result.winner === 'light') { lightRating.wins++; darkRating.losses++; }
          else if (result.winner === 'dark') { darkRating.wins++; lightRating.losses++; }
          else { lightRating.draws++; darkRating.draws++; }

          // Persist to Firebase (fire and forget)
          if (this.firebaseEnabled) {
            this.persistGameToFirebase(
              result, matchup.light, matchup.dark,
              lightRating, darkRating, exchange
            ).catch(() => {});
          }

          this.state.completedMatchups++;
          this.notify();
        }
      }

      this.state.status = 'finished';
      this.state.currentMatchup = 'Tournament complete';
      this.notify();

      // Save tournament summary to Firebase
      if (this.firebaseEnabled) {
        const finalRatings: Record<string, number> = {};
        for (const r of ratings) {
          const variantId = this.makeVariantId(r.variant);
          finalRatings[variantId] = r.rating;
        }
        AIArenaFirebaseService.saveTournament({
          variants: variants.map(v => this.makeVariantId(v)),
          gamesPerMatchup,
          totalGames: matchups.length * gamesPerMatchup,
          completedGames: this.state.completedMatchups,
          status: 'finished',
          startedAt: new Date(), // approximate
          finishedAt: new Date(),
          finalRatings,
        }).catch(() => { /* non-fatal */ });
      }
    } catch (err) {
      if (!signal.aborted) {
        this.state.status = 'idle';
        this.notify();
      }
    }
  }

  private statusBeforePause: ArenaStatus = 'idle';

  pause(): void {
    if (this.state.status === 'running' || this.state.status === 'continuous') {
      this.statusBeforePause = this.state.status;
      this.state.status = 'paused';
      this.notify();
    }
  }

  resume(): void {
    if (this.state.status === 'paused') {
      this.state.status = this.statusBeforePause === 'continuous' ? 'continuous' : 'running';
      this.notify();
    }
  }

  stop(): void {
    this.stopWatchdog();
    this.abortController?.abort();
    this.state.status = 'idle';
    this.state.currentMatchup = '';
    this.notify();
  }

  reset(): void {
    this.stop();
    this.state = this.createEmptyState(this.state.gamesPerMatchup);
    this.notify();
  }

  /**
   * Continuous random play with concurrent lanes.
   * Each lane independently picks two random variants, plays a game,
   * updates ELO, then repeats. All lanes share the same ratings array
   * (safe because JS is single-threaded — no data races).
   */
  async startContinuousPlay(
    allVariants: ArenaVariant[],
    concurrency: number = 1,
  ): Promise<void> {
    if (this.state.status === 'running' || this.state.status === 'continuous') return;
    if (allVariants.length < 2) return;

    this.tournamentId = `continuous-${Date.now()}`;
    const lanes = Math.max(1, Math.min(concurrency, 8));
    this.continuousVariants = allVariants;
    this.continuousConcurrency = lanes;
    this.lastGameCompletedAt = Date.now();

    // Init ratings for all variants
    const ratings: VariantRating[] = allVariants.map(v => ({
      variant: v,
      rating: START_RATING,
      games: 0,
      wins: 0,
      losses: 0,
      draws: 0,
      blunders: 0,
    }));

    // Try to seed from Firebase so ELO picks up where it left off
    try {
      const fbRatings = await AIArenaFirebaseService.getModelRatings();
      for (const fb of fbRatings) {
        const match = ratings.find(r =>
          this.makeVariantId(r.variant) === fb._docId
        );
        if (match) {
          match.rating = fb.rating;
          match.games = fb.games;
          match.wins = fb.wins;
          match.losses = fb.losses;
          match.draws = fb.draws;
          match.blunders = Math.round(fb.avgBlundersPerGame * fb.games);
        }
      }
    } catch { /* start fresh if Firebase unavailable */ }

    this.state = {
      status: 'continuous',
      ratings,
      blunders: [],
      matchHistory: [],
      currentMatchup: `Starting ${lanes} lane${lanes > 1 ? 's' : ''}...`,
      currentMoveCount: 0,
      totalMatchups: 0,
      completedMatchups: 0,
      currentGameInMatchup: lanes,
      gamesPerMatchup: 0,
    };
    this.notify();

    this.abortController = new AbortController();
    const signal = this.abortController.signal;

    // Start inactivity watchdog — auto-restarts lanes if they all stall
    this.startWatchdog();

    // Launch N concurrent game lanes
    const lanePromises = Array.from({ length: lanes }, (_, i) =>
      this.runContinuousLane(i, allVariants, ratings, signal)
    );

    await Promise.all(lanePromises);
  }

  /** A single continuous game lane — runs games in a loop until aborted. */
  private async runContinuousLane(
    laneIndex: number,
    allVariants: ArenaVariant[],
    ratings: VariantRating[],
    signal: AbortSignal,
  ): Promise<void> {
    let consecutiveFailures = 0;
    const MAX_CONSECUTIVE_FAILURES = 5;

    try {
      while (!signal.aborted) {
        // Pause support
        while (this.state.status === 'paused') {
          await this.sleep(200);
          if (signal.aborted) return;
        }

        // Pick two variants: prefer unplayed matchups + close ELO
        const [lightVariant, darkVariant] = this.pickWeightedMatchup(allVariants, ratings);

        this.state.currentMatchup = `${lightVariant.label} vs ${darkVariant.label}`;
        this.state.currentMoveCount = 0;
        this.notify();

        // Play with a per-game timeout to catch stuck AI
        let result: MatchResult | null = null;
        try {
          result = await this.withTimeout(
            this.playGame(lightVariant, darkVariant, signal),
            this.gameTimeoutMs,
            `Game timed out: ${lightVariant.label} vs ${darkVariant.label}`
          );
        } catch (err) {
          if (signal.aborted) return;
        }

        // If game failed, back off and retry instead of killing the lane
        if (!result) {
          if (signal.aborted) return;
          consecutiveFailures++;
          if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
            await this.sleep(3000);
            consecutiveFailures = 0;
          } else {
            await this.sleep(500);
          }
          continue;
        }

        // Game succeeded — reset failure counter
        consecutiveFailures = 0;
        this.lastGameCompletedAt = Date.now();

        // Record result (shared state, safe due to single-threaded JS)
        this.state.matchHistory.push(result);

        // Update ELO
        const lightRating = ratings.find(r => r.variant.label === lightVariant.label)!;
        const darkRating = ratings.find(r => r.variant.label === darkVariant.label)!;

        const eloResult = result.winner === 'light' ? 'a' : result.winner === 'dark' ? 'b' : 'draw';
        const exchange = calculateRatingExchange(lightRating.rating, darkRating.rating, eloResult);
        lightRating.rating += exchange.changeA;
        darkRating.rating += exchange.changeB;

        lightRating.games++;
        darkRating.games++;
        lightRating.blunders += result.lightBlunders;
        darkRating.blunders += result.darkBlunders;

        if (result.winner === 'light') { lightRating.wins++; darkRating.losses++; }
        else if (result.winner === 'dark') { darkRating.wins++; lightRating.losses++; }
        else { lightRating.draws++; darkRating.draws++; }

        // Persist to Firebase (fire and forget)
        if (this.firebaseEnabled) {
          this.persistGameToFirebase(
            result, lightVariant, darkVariant,
            lightRating, darkRating, exchange
          ).catch(() => {});
        }

        this.state.completedMatchups++;
        this.notify();

        // Brief stagger between games so lanes don't lock-step
        await this.sleep(50 + laneIndex * 30);
      }
    } catch (err) {
      if (!signal.aborted) {
      }
    }
  }

  // --- Private ---

  private async playGame(
    lightVariant: ArenaVariant,
    darkVariant: ArenaVariant,
    signal: AbortSignal
  ): Promise<MatchResult | null> {
    // Create a fresh engine for this game
    const engine = new ZodiacEngine();
    engine.setVerboseLogging(false);

    // Eligible pieces for arena armies (no Virus)
    const eligiblePieces = [
      PieceType.Bear, PieceType.Crane, PieceType.Frog, PieceType.Fungi,
      PieceType.Goat, PieceType.Monkey, PieceType.Octopus, PieceType.Rhino,
      PieceType.Snake, PieceType.Tree, PieceType.Wolf,
    ];

    // Model-aware army selection: use built-in heuristics where they exist
    let lightKing: PieceType;
    let darkKing: PieceType;
    try {
      const lightArmy = await this.selectArmyForModel(lightVariant.model, eligiblePieces);
      const darkArmy = await this.selectArmyForModel(darkVariant.model, eligiblePieces, [lightArmy.king, ...lightArmy.pieces]);
      lightKing = lightArmy.king;
      darkKing = darkArmy.king;

      // Init engine without random placement, then place manually
      engine.initForArena(lightKing, darkKing);
      this.placeArmy(engine, lightArmy, true);
      this.placeArmy(engine, darkArmy, false);
      engine.finalizeArenaSetup();
    } catch {
      // Fallback to random placement on any error
      const eligibleKings = eligiblePieces.filter(
        p => p !== PieceType.Octopus && p !== PieceType.Penguin
      );
      lightKing = eligibleKings[Math.floor(Math.random() * eligibleKings.length)];
      darkKing = eligibleKings[Math.floor(Math.random() * eligibleKings.length)];
      engine.switchToTestAIMode('arena-light', lightKing, darkKing, null);
    }

    // Create orchestrator for this engine
    const orchestrator = new AIOrchestrator(engine);
    const lightType = getBaseAITypeFromModel(lightVariant.model);
    const darkType = getBaseAITypeFromModel(darkVariant.model);
    orchestrator.setLightPlayer(lightType);
    orchestrator.setDarkPlayer(darkType);

    // Configure minimax model if needed
    this.configureMinimaxModel(orchestrator, lightVariant.model, lightType);
    this.configureMinimaxModel(orchestrator, darkVariant.model, darkType);

    // No per-move time limits — let AI think naturally.
    // The 90s per-game timeout is the safety net for stuck AI.

    let moveCount = 0;
    let lightBlunders = 0;
    let darkBlunders = 0;
    let lightBrilliants = 0;
    let darkBrilliants = 0;
    const keyPositions: KeyPosition[] = [];
    const notableMoves: NotableMove[] = [];
    const evalHistory: number[] = [];

    // Eval-based blunder/brilliant thresholds (tunable)
    const BLUNDER_THRESHOLD = -15;  // Lost ~1.5 pieces worth of value
    const BRILLIANT_THRESHOLD = 20; // Gained ~2 pieces worth of value

    // Capture opening FEN and eval
    const openingState = engine.getGameState();
    const openingEval = SentinelEvaluator.getAbsoluteBoardValue(openingState);
    try {
      keyPositions.push({ moveNum: 0, fen: ZodiacFEN.encode(openingState), eval: openingEval });
    } catch { /* non-fatal */ }

    // Game loop
    while (moveCount < this.safetyMoveLimit) {
      if (signal.aborted) return null;

      const gameState = engine.getGameState();
      if (gameState.state !== State.Playing) break;

      const isLightTurn = gameState.isLightTurn;

      try {
        const currentTimeLimit = isLightTurn ? lightVariant.timeLimit : darkVariant.timeLimit;
        const moveResult = await orchestrator.requestAIMove(gameState, currentTimeLimit);
        if (!moveResult) {
          break;
        }

        const confidence = moveResult.confidence;

        // Compute pre-move evaluation (always from Light's perspective)
        const preEval = SentinelEvaluator.getAbsoluteBoardValue(gameState);

        // Gather move details before executing
        const fromSquare = gameState.board[moveResult.fromPosition.row][moveResult.fromPosition.col];
        const piece = fromSquare.Pieces.find(p => p.id === moveResult.move.pieceId);
        if (!piece) {
          break;
        }

        const fromPos = moveResult.fromPosition;
        const toPos = moveResult.move.position;
        const colLetters = 'ABCDE';
        const fromLabel = `${colLetters[fromPos.col]}${fromPos.row + 1}`;
        const toLabel = `${colLetters[toPos.col]}${toPos.row + 1}`;
        const pieceName = piece.type as string;
        const moveType = moveResult.move.moveType as string;

        // Check for captured piece
        let capturedName: string | undefined;
        if (moveResult.move.toPiece) {
          capturedName = moveResult.move.toPiece.type as string;
        }

        // Execute the move
        await engine.makeMove(
          fromPos,
          toPos,
          piece,
          moveResult.move.toPiece,
          moveResult.move.moveType,
          moveResult.move.sideEffects
        );

        // Compute post-move evaluation
        const postEval = SentinelEvaluator.getAbsoluteBoardValue(engine.getGameState());
        evalHistory.push(postEval);

        // Eval swing from the moving side's perspective:
        // Positive = move helped the mover, Negative = move hurt the mover
        const evalSwing = postEval - preEval;
        const sideSwing = isLightTurn ? evalSwing : -evalSwing;

        const isBlunder = sideSwing <= BLUNDER_THRESHOLD;
        const isBrilliant = sideSwing >= BRILLIANT_THRESHOLD;
        const isNotable = isBlunder || isBrilliant;

        // Capture FENs for notable moves
        let preFen = '';
        let postFen = '';
        if (isNotable) {
          try { preFen = ZodiacFEN.encode(gameState); } catch { /* non-fatal */ }
          try { postFen = ZodiacFEN.encode(engine.getGameState()); } catch { /* non-fatal */ }
        }

        // Record blunder (eval swing shows the move hurt the mover)
        if (isBlunder) {
          const variantLabel = isLightTurn ? lightVariant.label : darkVariant.label;
          const opponentLabel = isLightTurn ? darkVariant.label : lightVariant.label;
          if (isLightTurn) lightBlunders++;
          else darkBlunders++;

          this.state.blunders.push({
            variantLabel,
            gameIndex: this.state.matchHistory.length,
            moveNumber: moveCount + 1,
            confidence,
            reasoning: moveResult.reasoning || `Eval swing: ${sideSwing > 0 ? '+' : ''}${sideSwing}`,
            opponent: opponentLabel,
          });

          notableMoves.push({
            moveNum: moveCount + 1,
            type: 'blunder',
            side: isLightTurn ? 'light' : 'dark',
            confidence,
            fen: postFen,
            preFen,
            postFen,
            pieceMoved: pieceName,
            from: fromLabel,
            to: toLabel,
            moveType,
            captured: capturedName,
            reasoning: moveResult.reasoning || 'Unknown',
            evalBefore: preEval,
            evalAfter: postEval,
          });
        }

        // Record brilliant (eval swing shows a strong gain for the mover)
        if (isBrilliant) {
          if (isLightTurn) lightBrilliants++;
          else darkBrilliants++;

          if (!preFen) {
            try { preFen = ZodiacFEN.encode(gameState); } catch { /* non-fatal */ }
          }
          if (!postFen) {
            try { postFen = ZodiacFEN.encode(engine.getGameState()); } catch { /* non-fatal */ }
          }

          notableMoves.push({
            moveNum: moveCount + 1,
            type: 'brilliant',
            side: isLightTurn ? 'light' : 'dark',
            confidence,
            fen: postFen,
            preFen,
            postFen,
            pieceMoved: pieceName,
            from: fromLabel,
            to: toLabel,
            moveType,
            captured: capturedName,
            reasoning: moveResult.reasoning || 'Unknown',
            evalBefore: preEval,
            evalAfter: postEval,
          });
        }

        moveCount++;

        // Capture midgame FEN every 15 moves
        if (moveCount % 15 === 0) {
          try {
            const midState = engine.getGameState();
            keyPositions.push({
              moveNum: moveCount,
              fen: ZodiacFEN.encode(midState),
              eval: postEval,
            });
          } catch { /* non-fatal */ }
        }

        // Yield to UI every 5 moves and broadcast move count
        if (moveCount % 5 === 0) {
          this.state.currentMoveCount = moveCount;
          this.notify();
          await this.sleep(0);
        }
      } catch (err) {
        break;
      }
    }

    // Determine winner and win condition
    const finalState = engine.getGameState();
    let winner: 'light' | 'dark' | 'draw';
    let winCondition: WinCondition = 'stalemate';

    if (finalState.lightWon === true) {
      winner = 'light';
      winCondition = this.detectWinCondition(finalState, true);
    } else if (finalState.lightWon === false) {
      winner = 'dark';
      winCondition = this.detectWinCondition(finalState, false);
    } else {
      winner = 'draw';
      winCondition = (finalState as any).drawReason === '3 time repeat draw' ? 'repetition' : 'stalemate';
    }

    // Record piece stats for both AI models (non-blocking, non-fatal)
    try {
      if (winner !== 'draw') {
        const lightWon = winner === 'light';
        PieceStatsCollector.recordGameResult(lightVariant.model, true, finalState, lightWon);
        PieceStatsCollector.recordGameResult(darkVariant.model, false, finalState, !lightWon);
      }
    } catch { /* non-fatal — don't break arena for stats */ }

    // Capture final FEN with eval
    const finalEval = SentinelEvaluator.getAbsoluteBoardValue(finalState);
    try {
      keyPositions.push({
        moveNum: moveCount,
        fen: ZodiacFEN.encode(finalState),
        eval: finalEval,
      });
    } catch { /* non-fatal */ }

    // Fix: The engine doesn't flush the final move's FEN to fenHistory
    // because changeTurns() is never called when the game ends.
    // Manually append the final position so replays show the complete game.
    const finalFen = ZodiacFEN.encode(finalState);
    if (finalState.fenHistory.length === 0 ||
        finalState.fenHistory[finalState.fenHistory.length - 1] !== finalFen) {
      finalState.fenHistory = [...finalState.fenHistory, finalFen];
    }

    return {
      lightVariant: lightVariant.label,
      darkVariant: darkVariant.label,
      winner,
      winCondition,
      moves: moveCount,
      lightBlunders,
      darkBlunders,
      lightBrilliants,
      darkBrilliants,
      keyPositions,
      notableMoves,
      fenHistory: finalState.fenHistory,
      evalHistory,
      lightKing: lightKing,
      darkKing: darkKing,
    };
  }

  // ─── Zenith king scoring (inlined from UltimateAI) ────────────
  private static readonly ZENITH_KING_SCORES: Partial<Record<PieceType, number>> = {
    [PieceType.Crane]: 90, [PieceType.Monkey]: 85, [PieceType.Frog]: 82,
    [PieceType.Snake]: 75, [PieceType.Wolf]: 72, [PieceType.Rhino]: 70,
    [PieceType.Tree]: 65, [PieceType.Goat]: 60, [PieceType.Bear]: 55,
    [PieceType.Fungi]: 45, [PieceType.Virus]: 40, [PieceType.Octopus]: 35,
  };

  /**
   * Select an army using the appropriate strategy for this AI model.
   * - Minimax models: built-in counter/synergy heuristic (PieceSelector)
   * - Zenith/Wisp: built-in king scoring + experience learning for pieces
   * - Others: full experience-weighted learning (UCB1)
   */
  private async selectArmyForModel(
    model: AIModel,
    eligiblePieces: PieceType[],
    opponentPieces?: PieceType[]
  ): Promise<{ king: PieceType; pieces: PieceType[]; columns: number[] }> {
    const config = AI_MODEL_CONFIG[model];
    const baseType = config.baseType;

    // Eligible kings: no Virus/Octopus/Penguin
    const ineligibleKings = new Set([PieceType.Virus, PieceType.Octopus, PieceType.Penguin]);
    const eligibleKings = eligiblePieces.filter(p => !ineligibleKings.has(p));

    // ── MINIMAX models: UCB1 king + PieceSelector heuristic (with opponent data) + learned columns ──
    if (baseType === AIPlayerType.MINIMAX) {
      const king = await ExperienceWeightedSelector.selectKingForAI(eligibleKings, model);
      const pieces: PieceType[] = [];
      for (let i = 0; i < 4; i++) {
        const available = eligiblePieces.filter(p => p !== king && !pieces.includes(p));
        const pick = selectPieceStrategic(available, opponentPieces ?? [], [king, ...pieces]);
        pieces.push(pick);
      }
      // Learned column assignment (same pattern as experience-weighted models)
      const cache = PieceStatsCache.getInstance();
      const stats = await cache.getStats(model);
      const allPieces = [king, ...pieces];
      const columns = this.assignLearnedColumns(allPieces, stats);
      return { king, pieces, columns };
    }

    // ── ULTIMATE models (Zenith/Weaver): built-in king scoring ──
    if (baseType === AIPlayerType.ULTIMATE) {
      let bestKing = eligibleKings[0];
      let bestScore = -1;
      for (const k of eligibleKings) {
        const s = ArenaService.ZENITH_KING_SCORES[k] || 50;
        if (s > bestScore) { bestScore = s; bestKing = k; }
      }
      const result = await ExperienceWeightedSelector.selectArmyPiecesForAI(
        model, bestKing, eligiblePieces, undefined, opponentPieces
      );
      return { king: bestKing, pieces: result.pieces, columns: result.columns };
    }

    // ── WISP: race-focused king scoring ──
    if (baseType === AIPlayerType.WISP) {
      let bestKing = eligibleKings[0];
      let bestScore = -1;
      for (const k of eligibleKings) {
        const s = KING_RACE_SCORES[k] || 50;
        if (s > bestScore) { bestScore = s; bestKing = k; }
      }
      const result = await ExperienceWeightedSelector.selectArmyPiecesForAI(
        model, bestKing, eligiblePieces, undefined, opponentPieces
      );
      return { king: bestKing, pieces: result.pieces, columns: result.columns };
    }

    // ── All others (GAIA, ALCHEMIST, CREATURE, etc.): full experience learning ──
    return ExperienceWeightedSelector.selectFullArmyForAI(model, eligiblePieces, undefined, opponentPieces);
  }

  /**
   * Assign columns using learned stats (reuses ExperienceWeightedSelector.selectColumn).
   * Pieces with ally adjacency preferences are placed first.
   */
  private assignLearnedColumns(
    allPieces: PieceType[],
    stats: any
  ): number[] {
    const pieceIndices = allPieces.map((_: any, i: number) => i);
    pieceIndices.sort((a: number, b: number) => {
      const aHasPrefs = hasAllyAdjacencyPrefs(allPieces[a]) ? 0 : 1;
      const bHasPrefs = hasAllyAdjacencyPrefs(allPieces[b]) ? 0 : 1;
      return aHasPrefs - bHasPrefs;
    });

    const columns: number[] = new Array(allPieces.length);
    const remainingCols = [0, 1, 2, 3, 4];
    const allyPlacements = new Map<PieceType, number>();

    for (const idx of pieceIndices) {
      const piece = allPieces[idx];
      const col = ExperienceWeightedSelector.selectColumn(piece, remainingCols, stats, allyPlacements);
      columns[idx] = col;
      allyPlacements.set(piece, col);
      remainingCols.splice(remainingCols.indexOf(col), 1);
    }

    return columns;
  }

  /**
   * Place a pre-selected army on the board via createPiece.
   * columns[0] is the king's column, columns[1..4] are army piece columns.
   */
  private placeArmy(
    engine: ZodiacEngine,
    army: { king: PieceType; pieces: PieceType[]; columns: number[] },
    isLight: boolean
  ): void {
    const row = isLight ? 0 : 7;
    // Place king (index 0)
    const kingPiece = engine.createPiece(army.king, isLight, { row, col: army.columns[0] });
    kingPiece.isKing = true;
    // Place army pieces (indices 1-4)
    for (let i = 0; i < army.pieces.length; i++) {
      engine.createPiece(army.pieces[i], isLight, { row, col: army.columns[i + 1] });
    }
  }

  private configureMinimaxModel(
    orchestrator: AIOrchestrator,
    model: AIModel,
    playerType: AIPlayerType
  ): void {
    const config = AI_MODEL_CONFIG[model];
    if (config.minimaxModel && playerType === AIPlayerType.MINIMAX) {
      const minimaxAI = orchestrator.getAI(AIPlayerType.MINIMAX) as any;
      if (minimaxAI?.setModel) {
        minimaxAI.setModel(config.minimaxModel);
      }
    }
  }

  private makeVariantId(variant: ArenaVariant): string {
    if (variant.timeFrame) {
      return `${variant.model.toLowerCase()}-${variant.timeFrame}`;
    }
    return variant.model.toLowerCase();
  }

  private detectWinCondition(gameState: any, lightWon: boolean): WinCondition {
    // Check if the winning king reached the opponent's back rank (race)
    // Light king races to row 7, dark king races to row 0
    try {
      for (let row = 0; row < gameState.board.length; row++) {
        for (let col = 0; col < gameState.board[row].length; col++) {
          const square = gameState.board[row][col];
          for (const piece of square.Pieces) {
            if (piece.isKing) {
              if (lightWon && piece.isLight && row === 7) return 'race';
              if (!lightWon && !piece.isLight && row === 0) return 'race';
            }
          }
        }
      }
    } catch { /* fallback to capture */ }
    return 'capture';
  }

  private async persistGameToFirebase(
    result: MatchResult,
    lightVariant: ArenaVariant,
    darkVariant: ArenaVariant,
    lightRating: VariantRating,
    darkRating: VariantRating,
    exchange: { changeA: number; changeB: number },
  ): Promise<void> {
    const lightId = this.makeVariantId(lightVariant);
    const darkId = this.makeVariantId(darkVariant);

    // Save game record
    const gameDoc: AIGameDoc = {
      tournamentId: this.tournamentId,
      lightVariant: lightId,
      darkVariant: darkId,
      winner: result.winner,
      winCondition: result.winCondition,
      moveCount: result.moves,
      lightBlunders: result.lightBlunders,
      darkBlunders: result.darkBlunders,
      lightBrilliants: result.lightBrilliants,
      darkBrilliants: result.darkBrilliants,
      lightEloChange: exchange.changeA,
      darkEloChange: exchange.changeB,
      keyPositions: result.keyPositions || [],
      notableMoves: result.notableMoves || [],
      fenHistory: result.fenHistory || [],
      evalHistory: result.evalHistory || [],
      lightKing: result.lightKing || '',
      darkKing: result.darkKing || '',
      timestamp: new Date(),
    };
    await AIArenaFirebaseService.saveGameResult(gameDoc);

    // Update model ratings
    const lightResult = result.winner === 'light' ? 'win' : result.winner === 'dark' ? 'loss' : 'draw' as const;
    const darkResult = result.winner === 'dark' ? 'win' : result.winner === 'light' ? 'loss' : 'draw' as const;
    const winCond = result.winner !== 'draw' ? result.winCondition : null;

    await Promise.all([
      AIArenaFirebaseService.updateModelRating(
        lightId, lightVariant.label, lightVariant.model, lightVariant.timeFrame || '',
        lightRating.rating, exchange.changeA, lightResult,
        result.moves, result.lightBlunders, result.lightBrilliants, winCond
      ),
      AIArenaFirebaseService.updateModelRating(
        darkId, darkVariant.label, darkVariant.model, darkVariant.timeFrame || '',
        darkRating.rating, exchange.changeB, darkResult,
        result.moves, result.darkBlunders, result.darkBrilliants, winCond
      ),
    ]);
  }

  /**
   * Weighted matchup picker for continuous play.
   * Builds all possible pairs, scores them by:
   *   1. Novelty — fewer games between this pair = higher weight
   *   2. ELO proximity — closer ratings = higher weight (more informative)
   * Then picks one pair via weighted random selection.
   */
  private pickWeightedMatchup(
    allVariants: ArenaVariant[],
    ratings: VariantRating[],
  ): [ArenaVariant, ArenaVariant] {
    // Build a map of games played per ordered pair
    const pairCounts = new Map<string, number>();
    for (const m of this.state.matchHistory) {
      const key = `${m.lightVariant}|${m.darkVariant}`;
      pairCounts.set(key, (pairCounts.get(key) || 0) + 1);
    }

    // Build rating lookup
    const ratingOf = new Map<string, number>();
    for (const r of ratings) {
      ratingOf.set(r.variant.label, r.rating);
    }

    // Score all possible ordered pairs (A as light, B as dark)
    const pairs: { light: ArenaVariant; dark: ArenaVariant; weight: number }[] = [];

    for (let i = 0; i < allVariants.length; i++) {
      for (let j = 0; j < allVariants.length; j++) {
        if (i === j) continue;
        const a = allVariants[i];
        const b = allVariants[j];

        const key = `${a.label}|${b.label}`;
        const gamesPlayed = pairCounts.get(key) || 0;

        // Novelty: unplayed pairs get 10x weight, then tapers off
        const noveltyWeight = 1 / (1 + gamesPlayed * 0.5);

        // ELO proximity: closer ELO = more weight (sigmoid-ish curve)
        const rA = ratingOf.get(a.label) || START_RATING;
        const rB = ratingOf.get(b.label) || START_RATING;
        const eloDiff = Math.abs(rA - rB);
        const eloWeight = 1 / (1 + eloDiff / 150);

        // Combined weight (novelty matters more early, ELO matters more later)
        const weight = (noveltyWeight * 3 + eloWeight) * (0.5 + Math.random() * 0.5);
        pairs.push({ light: a, dark: b, weight });
      }
    }

    // Weighted random selection
    const totalWeight = pairs.reduce((sum, p) => sum + p.weight, 0);
    let roll = Math.random() * totalWeight;
    for (const pair of pairs) {
      roll -= pair.weight;
      if (roll <= 0) return [pair.light, pair.dark];
    }

    // Fallback (shouldn't happen)
    return [pairs[0].light, pairs[0].dark];
  }

  /** Race a promise against a timeout. Rejects if the timeout fires first. */
  private withTimeout<T>(promise: Promise<T>, ms: number, msg: string): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error(msg)), ms);
      promise
        .then(v => { clearTimeout(timer); resolve(v); })
        .catch(e => { clearTimeout(timer); reject(e); });
    });
  }

  /** Start a watchdog that detects when no games have completed for too long and auto-restarts lanes. */
  private startWatchdog(): void {
    this.stopWatchdog();
    const INACTIVITY_LIMIT_MS = 120_000; // 2 minutes with no completed game = stalled

    this.watchdogTimer = setInterval(() => {
      if (this.state.status !== 'continuous') return;
      if (this.lastGameCompletedAt === 0) return; // haven't finished first game yet

      const elapsed = Date.now() - this.lastGameCompletedAt;
      if (elapsed > INACTIVITY_LIMIT_MS) {
        this.restartContinuousLanes();
      }
    }, 15_000); // check every 15s
  }

  private stopWatchdog(): void {
    if (this.watchdogTimer) {
      clearInterval(this.watchdogTimer);
      this.watchdogTimer = null;
    }
  }

  /** Kill current lanes and relaunch them without losing accumulated state. */
  private restartContinuousLanes(): void {
    if (this.state.status !== 'continuous' || this.continuousVariants.length < 2) return;

    // Abort current lanes
    this.abortController?.abort();

    // Relaunch
    this.abortController = new AbortController();
    const signal = this.abortController.signal;
    const lanes = this.continuousConcurrency;
    const ratings = this.state.ratings;

    this.lastGameCompletedAt = Date.now(); // reset so watchdog doesn't immediately re-trigger
    this.state.currentMatchup = `Restarting ${lanes} lane${lanes > 1 ? 's' : ''}...`;
    this.notify();

    const lanePromises = Array.from({ length: lanes }, (_, i) =>
      this.runContinuousLane(i, this.continuousVariants, ratings, signal)
    );

    Promise.all(lanePromises).catch(() => { /* handled in lanes */ });
  }

  private createEmptyState(gamesPerMatchup: number): ArenaState {
    return {
      status: 'idle',
      ratings: [],
      blunders: [],
      matchHistory: [],
      currentMatchup: '',
      currentMoveCount: 0,
      totalMatchups: 0,
      completedMatchups: 0,
      currentGameInMatchup: 0,
      gamesPerMatchup,
    };
  }

  private notify(): void {
    const snapshot = { ...this.state };
    this.listeners.forEach(fn => fn(snapshot));
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
